import { useMemo } from 'react';
import { useDroppable } from '@dnd-kit/core';
import { parseISO } from 'date-fns';
import type { SprintDef } from '@/hooks/use-calendar';
import { cn } from '@/lib/utils';
import { extractIsoDate, type PeriodDef } from './calendar-config';

// ============================================
// CALENDAR DAY CELL
// ============================================
//
// t16: a single day cell used by the Month + Week views. Renders the
// day number, optional sprint/period bands, then the children (event
// pills) passed in by the parent. Doubles as a dnd-kit droppable
// target so dragging a pill from one cell to another triggers a
// date change.
//
// The cell itself does NOT render the events — the parent composes
// the children (which keeps the Month/Week grids independent of the
// dnd-kit wiring and lets the Agenda view skip both). What the cell
// owns: layout chrome + drop target id.

export interface CalendarDayCellProps {
    /** Stable id used by dnd-kit's useDroppable. */
    id: string;
    date: Date;
    isCurrentMonth?: boolean;
    isToday?: boolean;
    isWeekend?: boolean;
    sprints?: SprintDef[];
    /** T21 R4: user-defined periods that render as additional bands. */
    periods?: PeriodDef[];
    /** Compact mode — used in Week view for the tighter 7-day grid. */
    compact?: boolean;
    children?: React.ReactNode;
    className?: string;
    /** t18: right-click handler — used by Month/Week views to open the
     *  context menu anchored at the cursor. Called with the React event
     *  so the parent can call `preventDefault` + `stopPropagation`. */
    onContextMenu?: (event: React.MouseEvent<HTMLDivElement>) => void;
}

interface DayBand {
    id: string;
    name: string;
    color: string;
}

export function CalendarDayCell({
    id,
    date,
    isCurrentMonth,
    isToday,
    isWeekend,
    sprints,
    periods,
    compact,
    children,
    className,
    onContextMenu,
}: CalendarDayCellProps) {
    const { isOver, setNodeRef } = useDroppable({
        id,
        // t18: send the YYYY-MM-DD day-key directly so the drag handler
        // doesn't need to slice a UTC ISO timestamp and risk the day-1
        // shift. `format(date, 'yyyy-MM-dd')` uses local TZ which is
        // what the cell visually represents.
        data: {
            type: 'calendar-day',
            date: `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`,
        },
    });

    const bands = useMemo<DayBand[]>(() => {
        const dayStart = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
        const dayEnd = dayStart + 24 * 60 * 60 * 1000 - 1;
        const out: DayBand[] = [];
        if (sprints && sprints.length > 0) {
            for (const s of sprints) {
                const startKey = extractIsoDate(s.startDate);
                const endKey = extractIsoDate(s.endDate);
                if (!startKey || !endKey) continue;
                const sStart = parseISO(startKey).getTime();
                const sEnd = parseISO(endKey).getTime() + 24 * 60 * 60 * 1000 - 1;
                if (sStart <= dayEnd && sEnd >= dayStart) {
                    out.push({ id: `sprint-${s.id}`, name: s.name, color: s.color ?? '#a855f7' });
                }
            }
        }
        // T21 R4: periods render alongside sprints. We dedupe by id so
        // the same period doesn't appear twice when both Month and
        // Week pass it down.
        if (periods && periods.length > 0) {
            for (const p of periods) {
                const startKey = extractIsoDate(p.startDate);
                const endKey = extractIsoDate(p.endDate);
                if (!startKey || !endKey) continue;
                const pStart = parseISO(startKey).getTime();
                const pEnd = parseISO(endKey).getTime() + 24 * 60 * 60 * 1000 - 1;
                if (pStart <= dayEnd && pEnd >= dayStart) {
                    out.push({ id: `period-${p.id}`, name: p.name, color: p.color ?? '#6366f1' });
                }
            }
        }
        return out;
    }, [sprints, periods, date]);

    return (
        <div
            ref={setNodeRef}
            data-testid={`calendar-day-${id}`}
            onContextMenu={onContextMenu}
            className={cn(
                'relative flex flex-col border border-white/10 bg-surface/40 min-h-0',
                compact ? 'min-h-[120px]' : 'min-h-[110px]',
                !isCurrentMonth && 'bg-surface/20 text-foreground-muted',
                isWeekend && !isCurrentMonth && 'bg-surface/10',
                isOver && 'outline outline-2 outline-primary/60 outline-offset-[-2px] bg-primary/5',
                className
            )}
        >
            {/* Sprint / period bands behind the day number + events.
                Absolute so they don't push content; the parent grid
                sets overflow-hidden via the cell className if needed. */}
            {bands.length > 0 && (
                <div className="pointer-events-none absolute inset-x-0 top-0 bottom-0 z-0">
                    {bands.map((b, idx) => (
                        <div
                            key={b.id}
                            className={cn(
                                'absolute inset-x-0',
                                'opacity-30'
                            )}
                            style={{
                                backgroundColor: b.color,
                                top: `${idx * 18 + 6}px`,
                                height: '16px',
                            }}
                            title={b.name}
                        />
                    ))}
                </div>
            )}

            {/* T30 R6: smaller padding on mobile — px-1 instead of
                px-1.5, pt-0.5 instead of pt-1. The header still has
                room for the day-number badge + a short band name on
                screens ≥640px. */}
            <div className={cn(
                'relative z-10 flex items-center justify-between px-1 sm:px-1.5 pt-0.5 sm:pt-1 text-[10px] sm:text-[11px]',
                !isCurrentMonth && 'text-foreground-muted/70'
            )}>
                <span className={cn(
                    'inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1 sm:px-1.5 text-[10px] sm:text-[11px] font-medium',
                    isToday && 'bg-primary text-primary-foreground'
                )}>
                    {date.getDate()}
                </span>
                {bands.length > 0 && !compact && (
                    <span className="truncate text-[9px] sm:text-[10px] text-foreground-muted/80" title={bands.map((b) => b.name).join(', ')}>
                        {bands.map((b) => b.name).slice(0, 1).join(', ')}{bands.length > 1 ? ` +${bands.length - 1}` : ''}
                    </span>
                )}
            </div>

            {/* T30 R6: smaller padding on mobile (p-0.5) so more pills
                fit in a narrow cell. p-1 is restored on sm+. */}
            <div className={cn(
                'relative z-10 flex-1 space-y-0.5 sm:space-y-1 overflow-y-auto p-0.5 sm:p-1',
                compact ? 'max-h-[300px]' : 'max-h-[180px]'
            )}>
                {children}
            </div>
        </div>
    );
}
